import { useListingMatchVersions } from "./use-listing-match-versions";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { MatchMode } from "@/graphql/generated/operations";
import type { DashboardContext, KindToMatch } from "@/pages/project/dashboard-kinds";
import { parseRequirements, type ProjectRequirements } from "@/pages/project/project-plan";
import { ProjectCandidatesScreen, type CandidatePath } from "@/pages/project/screens/ProjectCandidatesScreen";
import { ProjectToMatchScreen } from "@/pages/project/screens/ProjectToMatchScreen";
import { useLinkedMatches } from "@/pages/project/use-linked-matches";
import { useListingApplicants } from "./use-listing-applicants";
import { listingSearchTarget } from "./listing-search-target";
import { hasListingRequirements, listingRequirementsData } from "./listing-requirements";
import { parseDocuments } from "@/pages/project/project-plan";
import { ListingRanking } from "./ListingRanking";

const NO_CANDIDATES: readonly string[] = [];
const NO_WAYS: readonly NonNullable<CandidatePath>[] = [];
const MATCH_ONLY: readonly NonNullable<CandidatePath>[] = ["match"];
// Dieselben Worte wie die Fläche "Bewerbungen" ohne Leserecht (ListingApplicationsNoAccess).
const APPLICANTS_WITHHELD = { id: "share.listings.applications.noAccess", defaultMessage: "Kein Zugriff auf Bewerbungen" };
const APPLICATIONS_WITHHELD = {
  id: "share.listings.toMatch.noApplicationAccess",
  defaultMessage:
    "Ohne Leserecht auf Bewerbungen gehen keine Bewerbenden mit und neue Bewerbungen werden nicht automatisch bewertet. Zugriff vergibt, wer die Zugriffe dieser Ausschreibung verwaltet.",
};

// Die Fläche "Match" einer Ausschreibung: ihre verknüpften Matches und der Weg in ein neues, das
// Titel, Beschreibung, Anforderungen und Bewerbende der Ausschreibung mitnimmt und danach mit ihr
// verknüpft ist (createMatch, dann linkListingMatch, dann die Bewerbenden als Kandidaten). Dieselben
// Bausteine wie beim Projekt, aber ohne Gefragte: eine Ausschreibung schreibt keine `data.candidates`,
// die Empfänger und Bewerbende mitlesen würden. Was der Schritt anbietet, steht in `toMatch` der Art.
//
// Wer verknüpfen und lösen darf, sagt `actions.canManageMatches` (WRITE auf `candidates`), nicht
// `canUpdate`: Kandidaten zu suchen und den Ausschreibungstext zu ändern sind getrennte Rechte.
export function ListingMatchFacet({
  ctx,
  view,
  onViewChange,
}: {
  ctx: DashboardContext;
  /** Die offene Ansicht; bei einer Ausschreibung gibt es nur den Weg ins Match. */
  view: string | null;
  onViewChange: (next: string | null) => void;
}) {
  const [creatingFirstMatch, setCreatingFirstMatch] = useState(false);
  const company = listingSearchTarget(ctx.data) === "COMPANIES";
  const canManage = ctx.entity.actions.canManageMatches;
  const linkedMatches = useLinkedMatches(ctx.entity.matchIds);
  const versions = useListingMatchVersions(ctx.entity.matchIds.length ? ctx.entity.id : undefined);
  const versionIds = new Set(versions.data?.listing?.matches?.filter((m) => m.listingVersion).map((m) => m.id));
  const legacyMatches = linkedMatches.filter((m) => !versionIds.has(m.id));
  const requirements = useMemo(() => parseRequirements(ctx.data.requirements), [ctx.data.requirements]);
  const unlinkMatch = ctx.source.unlinkMatch;
  const rankings = linkedMatches.filter((match) => match.matchMode === (company ? MatchMode.RankCompanies : MatchMode.Rank));
  const automatic = rankings.filter((match) => match.autoMatch);
  const primary = automatic.length > 0 ? automatic : rankings;
  const hasLinkedMatch = ctx.entity.matchIds.length > 0;
  const showCreation = !hasLinkedMatch || creatingFirstMatch;
  if (!hasLinkedMatch && !hasListingRequirements(listingRequirementsData(ctx.data))) {
    return (
      <div id="listing-match-needs-requirements" className="space-y-4 py-4">
        <div className="max-w-[62ch] space-y-2">
          <h3 className="text-smycles-title font-medium"><FormattedMessage id="share.listings.match.needsRequirements" /></h3>
          <p className="text-smycles-body text-ink"><FormattedMessage id={ctx.readOnly
            ? "share.listings.match.needsRequirementsReadOnly" : "share.listings.match.needsRequirementsHint"} /></p>
        </div>
        {!ctx.readOnly ? (
          <Button id="listing-match-add-requirements" onClick={() => { ctx.keepFacets(["team"]); ctx.openFacet("team"); }}>
            <FormattedMessage id="project.facet.team.add" />
          </Button>
        ) : null}
      </div>
    );
  }
  const management = (
    <ProjectCandidatesScreen
      showLatestResult
      ways={showCreation ? MATCH_ONLY : NO_WAYS}
      candidates={NO_CANDIDATES}
      onCandidatesChange={() => undefined}
      linkedMatches={legacyMatches}
      path={showCreation && view === "match" ? "match" : null}
      onPathChange={onViewChange}
      onUnlinkMatch={unlinkMatch && canManage ? unlinkMatch : undefined}
      readOnly={!canManage}
      texts={ctx.config.matchFacet}
      matchWizard={
        <ListingToMatch
          ctx={ctx}
          onStart={() => setCreatingFirstMatch(true)}
          requirements={requirements}
          link={canManage ? (ctx.source.linkMatch ?? null) : null}
        />
      }
    />
  );
  return !showCreation ? (
    <div className="space-y-6">
      {/* Eine neue oder erneut gestartete Version holt alle Bewerbenden in den Pool: dafür braucht es außer WRITE auf
          Kandidaten das Leserecht auf Bewerbungen, wie genesis es prüft (GENESIS-0096). */}
      <ListingRanking matches={primary} listingId={ctx.entity.id} versionsQuery={versions}
        canReadApplications={!!ctx.entity.actions.canReadApplications}
        canManage={canManage && Boolean(ctx.entity.actions.canReadApplications)}
        beforePrepare={ctx.flush}
        onOpenRequirements={() => ctx.openFacet("team")} />
      {legacyMatches.length > 0 ? <Disclosure title={<FormattedMessage id="share.listings.ranking.management" />}>
        {management}
      </Disclosure> : null}
    </div>
  ) : management;
}

/** Lädt die Bewerbenden erst, wenn der Weg ins Match offen ist, nicht schon beim Blick auf die Fläche. */
function ListingToMatch({
  ctx,
  onStart,
  requirements,
  link,
}: {
  ctx: DashboardContext;
  onStart: () => void;
  requirements: ProjectRequirements;
  link: ((matchId: string) => Promise<void>) | null;
}) {
  // Bewerbungen liest genesis nur vor, wer sie lesen darf, nicht schon jedem Mitglied
  // (@external-adr GENESIS-0096-listing-applications-are-read-only-by-their-explicit-readers); ohne das Recht
  // nimmt der Weg ins Match keine Bewerbenden mit. Die automatische Bewertung bietet er dann auch nicht an:
  // genesis schaltet sie nur für Lesende ein (GENESIS001274). Die Zeile sagt "kein Zugriff" statt "keine".
  const readsApplications = Boolean(ctx.entity.actions.canReadApplications);
  const applicants = useListingApplicants(ctx.entity.id, { skip: !readsApplications });
  const company = listingSearchTarget(ctx.data) === "COMPANIES";
  const toMatch: KindToMatch = company ? { ...ctx.config.toMatch, modes: ["RANK_COMPANIES"], autoMatch: true,
    takes: ["description", "requirements", "documents"], requiredTakes: ["requirements"],
    copyNote: { id: "share.listings.companyMatch.copyNote", defaultMessage: "Die Unternehmensanforderungen werden ins Match übernommen. Geänderte Anforderungen übernimmst du mit einer neuen Version." } } : ctx.config.toMatch;
  return (
    <ProjectToMatchScreen
      onStart={onStart}
      toMatch={readsApplications ? toMatch
        : { ...toMatch, autoMatch: false, people: { ...toMatch.people, detail: APPLICANTS_WITHHELD } }}
      withheld={readsApplications ? undefined : APPLICATIONS_WITHHELD}
      link={link}
      title={ctx.title}
      description={ctx.description}
      requirements={requirements}
      documents={parseDocuments(ctx.data.documents)}
      companies={company ? applicants.companies : undefined}
      requirementsData={listingRequirementsData(ctx.data)}
      companyRequirements={company ? listingRequirementsData(ctx.data) : undefined}
      people={company ? [] : applicants.people}
      peopleLoading={applicants.loading}
      peopleError={applicants.error}
    />
  );
}
